import { useEffect, useState, useRef } from 'react';

interface PhotoReviewProps {
  imageBlob: Blob;
  durationSeconds: number;
  shotNumber: number;
  totalShots: number;
  onComplete: () => void;
  /** Report countdown to parent so it can be shown in the right section (not on canvas) */
  onCountdownChange?: (value: number | null) => void;
}

export function PhotoReview({
  imageBlob,
  durationSeconds,
  shotNumber: _shotNumber,
  totalShots: _totalShots,
  onComplete,
  onCountdownChange,
}: PhotoReviewProps) {
  const [_countdown, setCountdown] = useState(durationSeconds);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(imageBlob);
    setImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setImageUrl(null);
    };
  }, [imageBlob]);

  useEffect(() => {
    setCountdown(durationSeconds);
    onCountdownChange?.(durationSeconds);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        const next = prev <= 1 ? 0 : prev - 1;
        onCountdownChange?.(next <= 0 ? null : next);
        if (prev <= 1) {
          clearInterval(interval);
          onCompleteRef.current();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => {
      clearInterval(interval);
      onCountdownChange?.(null);
    };
  }, [durationSeconds, onCountdownChange]);

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative w-full aspect-[3/4] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Captured photo"
            className="w-full h-full object-cover"
          />
        )}
      </div>
    </div>
  );
}
